import {
  checkArgument,
  checkExhaustive,
  checkExists,
  checkState,
} from 'external/dev_april_corgi+/js/common/asserts';
import {
  DefaultMap,
  getOnlyElement,
} from 'external/dev_april_corgi+/js/common/collections';

import {
  DataType,
  DataValue,
  PlatedMaterialValue,
  TableValue,
  WellId,
} from '../types/data';

import {ActionsDispatcher} from './actions_dispatcher';
import {DataConstraints, Graph, Node, NodeId, Pad, PipeId} from './graph';
import {LabState} from './lab_state';

export class PipedGraphOverlay {
  private readonly nextIds: DefaultMap<Node, () => PipeId>;
  private readonly notes: DefaultMap<
    NodeId,
    Array<{label: string; value: TableValue}>
  >;
  private readonly onSuccess: Array<() => Promise<void>>;
  private readonly pads: Map<Pad<'input' | 'output'>, Source<DataValue>>;
  private readonly padErrors: DefaultMap<Pad<'input' | 'output'>, string[]>;
  private readonly nodeErrors: DefaultMap<NodeId, string[]>;
  private readonly values: Map<
    Pad<'input'>,
    {value: DataValue; constraints: DataConstraints}
  >;

  constructor(
    private readonly dispatcher: ActionsDispatcher,
    private readonly graph: Graph,
  ) {
    this.nextIds = new DefaultMap(node => {
      const state = {count: 0};
      return () => ({id: String(++state.count), node: node.id});
    });
    this.notes = new DefaultMap(() => []);
    this.onSuccess = [];
    this.pads = new Map();
    this.padErrors = new DefaultMap(() => []);
    this.nodeErrors = new DefaultMap(() => []);
    this.values = new Map();
  }

  async commit(): Promise<void> {
    for (const invocation of this.onSuccess) {
      await invocation();
    }
    this.onSuccess.length = 0;
  }

  initialSources(): Array<Source<DataValue>> {
    const initial = [];
    for (const [pad, source] of this.pads) {
      if (pad.direction !== 'input') {
        continue;
      }
      if (pad.links.size > 0) {
        continue;
      }
      initial.push(source);
    }
    return initial;
  }

  nextIdFor(node: Node): PipeId {
    return this.nextIds.get(node)();
  }

  padToSources(pad: Pad<'input'>): Array<Source<DataValue>> {
    const sources: Array<Source<DataValue>> = [];
    if (pad.links.size === 0) {
      checkState(!this.pads.has(pad));
      const source = new UnlinkedPadSource(this.nextIdFor(pad.node), pad);
      this.pads.set(pad, source);
      sources.push(source);
    }

    for (const link of pad.links.values()) {
      sources.push(
        new LinkedPadSource(
          this.nextIdFor(pad.node),
          pad,
          checkExists(this.pads.get(link.output)),
        ),
      );
    }
    return sources;
  }

  errorsForPad(pad: Pad<'input' | 'output'>): string[] {
    return this.padErrors.get(pad);
  }

  errorsForNode(node: Node): string[] {
    return this.nodeErrors.get(node.id);
  }

  sourceForPad(pad: Pad<'input' | 'output'>): Source<DataValue> | undefined {
    return this.pads.get(pad);
  }

  notesForNode(node: NodeId): Array<{label: string; value: TableValue}> {
    return this.notes.get(node);
  }

  singleValuePadSource<V extends DataValue>(
    pad: Pad<'input'>,
    kind: V['kind'],
    factory: ((context: PipeContext) => V) | undefined = undefined,
  ): Source<V> {
    let roots;
    if (factory) {
      roots = [
        new OrElseSource(
          this.nextIdFor(pad.node),
          factory,
          this.padToSources(pad),
        ),
      ];
    } else {
      roots = this.padToSources(pad);
    }
    return new SingleValueTransformer(this.nextIdFor(pad.node), kind, roots);
  }

  addPad(pad: Pad<'input'> | Pad<'output'>): void {
    this.onSuccess.push(async () => {
      if (pad.direction === 'input') {
        pad.node.inputs.set(pad.id, pad);
      } else if (pad.direction === 'output') {
        pad.node.outputs.set(pad.id, pad);
      } else {
        throw checkExhaustive(pad);
      }
    });
  }

  addPadError(pad: Pad<'input'>, error: unknown): void {
    const errors = this.padErrors.get(pad);
    const message = String(
      typeof error === 'object' && error && 'message' in error
        ? error.message
        : error,
    );
    if (errors.indexOf(message) < 0) {
      errors.push(message);
    }
    this.dispatcher.failed(error);
  }

  addPipeError(pipe: PipeId, error: unknown): void {
    console.error(`Error in ${pipe.node}-${pipe.id}:`, error);
    const errors = this.nodeErrors.get(pipe.node);
    const message = String(
      typeof error === 'object' && error && 'message' in error
        ? error.message
        : error,
    );
    if (errors.indexOf(message) < 0) {
      errors.push(message);
    }
    this.dispatcher.failed(error);
  }

  addNodeNote(node: NodeId, label: string, value: TableValue): void {
    this.notes.get(node).push({label, value});
  }

  async removePad(pad: Pad<'input' | 'output'>): Promise<void> {
    for (const link of pad.links.values()) {
      await this.graph.deleteLink(link);
    }

    if (pad.direction === 'input') {
      pad.node.inputs.delete(pad.id);
    } else if (pad.direction === 'output') {
      pad.node.outputs.delete(pad.id);
    } else {
      throw checkExhaustive(pad.direction);
    }
  }

  setPadSource(pad: Pad<'output'>, source: Source<DataValue>): void {
    this.pads.set(pad, source);
  }

  setType(pad: Pad<'input' | 'output'>, type: DataType): void {
    pad.type = type;
  }

  clearValue(pad: Pad<'input'>): void {
    // We need to do this immediately or else pipes will pick up the value and repropagate it
    pad.unlinked = undefined;
    this.values.delete(pad);
  }

  setValue(
    pad: Pad<'input'>,
    value: DataValue,
    constraints: DataConstraints,
  ): void {
    this.values.set(pad, {value, constraints});
    this.onSuccess.push(async () => {
      pad.unlinked = {
        constraints,
        value,
      };
    });
  }
}

export interface PipeContext {
  changes: PipedGraphOverlay;
  dispatcher: ActionsDispatcher;
  lab: LabState;
}

export interface Source<O> {
  id: PipeId;
  act(context: PipeContext): Promise<void>;
  lastValue(): O | undefined;
  link(sink: Sink<O>): void;
  links(): Array<Sink<O>>;
}

export interface Sink<I> {
  id: PipeId;
  constrain(
    source: Source<I>,
    value: Readonly<I> | undefined,
    block: Promise<void>,
    context: PipeContext,
  ): Promise<DataConstraints>;
  parentAdded(): void;
  parentRemoved(): void;
}

export abstract class PSource<O> implements Source<O> {
  protected readonly sinks: Set<Sink<O>>;
  private block: {
    promise: Promise<void>;
    resolve: () => void;
    reject: (reason: unknown) => void;
  };
  private _lastValue: O | undefined;

  constructor(readonly id: PipeId) {
    this.sinks = new Set();

    this.block = {} as any;
    this.block.promise = new Promise<void>((resolve, reject) => {
      this.block.resolve = resolve;
      this.block.reject = reject;
    });
    this._lastValue = undefined;
  }

  protected abstract collate(context: PipeContext): Promise<void>;

  async act(context: PipeContext): Promise<void> {
    context.dispatcher.pipeBlockedOn(this, []);
    try {
      await this.collate(context);
      this.block.resolve();
    } catch (e: unknown) {
      context.changes.addPipeError(this.id, e);
      this.block.reject(e);
    } finally {
      context.dispatcher.pipeFinished(this);
    }
    // TODO(april): should we do this in PSink and PTransformer?
    return this.block.promise;
  }

  protected async gatherConstraints(
    value: Readonly<O> | undefined,
    context: PipeContext,
    includeValue?: boolean,
  ): Promise<DataConstraints> {
    this._lastValue = value;
    const promises = [];
    for (const sink of this.sinks) {
      promises.push(sink.constrain(this, value, this.block.promise, context));
    }
    const constraintss = await Promise.all(promises);
    const constraints: DataConstraints = {};
    for (const c of constraintss) {
      mergeConstraints(c, constraints);
    }
    if (includeValue !== true) {
      delete constraints.type;
      delete constraints.value;
    }
    return constraints;
  }

  lastValue(): O | undefined {
    return this._lastValue;
  }

  link(sink: Sink<O>): void {
    if (!this.sinks.has(sink)) {
      this.sinks.add(sink);
      sink.parentAdded();
    }
  }

  links(): Array<Sink<O>> {
    return [...this.sinks];
  }
}

export abstract class PSink<I> implements Sink<I> {
  protected readonly values: Map<
    Source<I>,
    {
      source: Source<I>;
      value: Readonly<I> | undefined;
      resolve: (value: DataConstraints) => Promise<void>;
      reject: (reason: unknown) => void;
    }
  >;

  private parents: number;

  constructor(
    readonly id: PipeId,
    protected readonly sources: Array<Source<I>>,
  ) {
    this.values = new Map();
    this.parents = 0;

    for (const source of sources) {
      source.link(this);
    }
  }

  protected abstract collate(context: PipeContext): Promise<void>;

  constrain(
    source: Source<I>,
    value: Readonly<I> | undefined,
    block: Promise<void>,
    context: PipeContext,
  ): Promise<DataConstraints> {
    const last = this.values.size + 1 === this.parents;
    const promise: Promise<DataConstraints> = new Promise((resolve, reject) => {
      this.values.set(source, {
        source,
        value,
        resolve: (constraints: DataConstraints) => {
          resolve(constraints);
          return block;
        },
        reject,
      });
    });
    if (last) {
      context.dispatcher.pipeBlockedOn(this, [...this.values.keys()]);
      this.collate(context)
        .finally(async () => {
          const promises = [];
          for (const {resolve} of this.values.values()) {
            promises.push(resolve({}));
          }
          await Promise.all(promises).catch(() => {});
        })
        .catch(reason => {
          context.changes.addPipeError(this.id, reason);
        })
        .finally(async () => {
          // TODO(april): we used to only do this as the first .then after collate. Was that better?
          context.dispatcher.pipeFinished(this);
        });
    }
    return promise;
  }

  parentAdded(): void {
    this.parents += 1;
  }

  parentRemoved(): void {
    this.parents -= 1;
  }
}

export abstract class PTransformer<I, O> implements Sink<I>, Source<O> {
  protected readonly sinks: Set<Sink<O>>;
  protected readonly values: Map<
    Source<I>,
    {
      source: Source<I>;
      value: Readonly<I> | undefined;
      resolve: (value: DataConstraints) => Promise<void>;
      reject: (reason: unknown) => void;
    }
  >;
  private block: {
    promise: Promise<void>;
    resolve: () => void;
    reject: (reason: unknown) => void;
  };
  private parents: number;
  private value: O | undefined;

  constructor(
    readonly id: PipeId,
    protected readonly sources: Array<Source<I>>,
  ) {
    this.sinks = new Set();
    this.values = new Map();
    this.parents = 0;

    this.block = {} as any;
    this.block.promise = new Promise<void>((resolve, reject) => {
      this.block.resolve = resolve;
      this.block.reject = reject;
    });
    this.value = undefined;

    for (const source of sources) {
      source.link(this);
    }
  }

  act(_context: PipeContext): Promise<void> {
    throw new Error('`act` should never be called');
  }

  lastValue(): O | undefined {
    return this.value;
  }

  protected abstract collate(context: PipeContext): Promise<void>;

  constrain(
    source: Source<I>,
    value: Readonly<I> | undefined,
    block: Promise<void>,
    context: PipeContext,
  ): Promise<DataConstraints> {
    if (this.values.has(source)) {
      throw new Error(
        `Value from ${source.id.node}-${source.id.id} provided twice`,
      );
    }
    const last = this.values.size + 1 === this.parents;
    const promise: Promise<DataConstraints> = new Promise((resolve, reject) => {
      this.values.set(source, {
        source,
        value,
        resolve: (constraints: DataConstraints) => {
          resolve(constraints);
          return block;
        },
        reject,
      });
    });
    if (last) {
      // TODO(april): one could imagine depping on all input plates... (see below comment)
      context.dispatcher.pipeBlockedOn(this, [...this.values.keys()]);
      this.collate(context)
        .finally(async () => {
          const promises = [];
          for (const {resolve} of this.values.values()) {
            promises.push(resolve({}));
          }
          await Promise.all(promises).catch(() => {});
        })
        .then(
          () => {
            this.block.resolve();
          },
          reason => {
            context.changes.addPipeError(this.id, reason);
          },
        )
        .finally(async () => {
          context.dispatcher.pipeFinished(this);
        });
      // and undepping here. Which would allow us to know when a plate is compiled and ready to be
      // executed.
      // TODO(april): that would be cool but it's unclear why it matters?
    }
    return promise;
  }

  protected async gatherConstraints(
    value: Readonly<O> | undefined,
    context: PipeContext,
    includeValue?: boolean,
  ): Promise<DataConstraints> {
    this.value = value;
    const promises = [];
    for (const sink of this.sinks) {
      promises.push(sink.constrain(this, value, this.block.promise, context));
    }
    const constraintss = await Promise.all(promises);
    const constraints: DataConstraints = {};
    for (const c of constraintss) {
      mergeConstraints(c, constraints);
    }
    if (includeValue !== true) {
      delete constraints.type;
      delete constraints.value;
    }
    return constraints;
  }

  link(sink: Sink<O>): void {
    if (!this.sinks.has(sink)) {
      this.sinks.add(sink);
      sink.parentAdded();
    }
  }

  links(): Array<Sink<O>> {
    return [...this.sinks];
  }

  parentAdded(): void {
    this.parents += 1;
  }

  parentRemoved(): void {
    this.parents -= 1;
  }
}

export class OrElseSource extends PTransformer<DataValue, DataValue> {
  constructor(
    id: PipeId,
    private readonly fn: (context: PipeContext) => DataValue,
    sources: Array<Source<DataValue>>,
  ) {
    super(id, sources);
  }

  async collate(context: PipeContext): Promise<void> {
    if (this.values.size === 0) {
      throw new Error('Expected one value');
    } else if (this.values.size > 1) {
      for (const {reject} of this.values.values()) {
        reject('Expected only one value');
      }
      throw new Error('Expected only one value');
    } else {
      const {value, resolve, reject} = getOnlyElement(this.values.values());
      const defaulted = value ?? this.fn(context);

      let constraints;
      try {
        constraints = await this.gatherConstraints(
          defaulted,
          context,
          /* includeValue= */ true,
        );
      } catch (e: unknown) {
        reject(e);
        return;
      }

      if (!value && !constraints.value) {
        constraints.value = {
          expected: defaulted,
          reason: 'Setting default value',
        };
      } else if (constraints.value === value) {
        delete constraints.value;
      }
      await resolve(constraints);
    }
  }
}

export class LinkedPadSource extends PTransformer<DataValue, DataValue> {
  constructor(
    id: PipeId,
    private readonly pad: Pad<'input'>,
    source: Source<DataValue>,
  ) {
    super(id, [source]);
  }

  async collate(context: PipeContext): Promise<void> {
    if (this.values.size !== 1) {
      context.changes.addPadError(
        this.pad,
        `Expected 1 value, got ${this.values.size}`,
      );
      for (const {resolve} of this.values.values()) {
        // Don't propagate the error higher
        resolve({});
      }
      return;
    }

    const {value, resolve, reject} = getOnlyElement(this.values.values());
    let constraints;
    try {
      constraints = await this.gatherConstraints(
        value,
        context,
        /* includeValue= */ true,
      );
    } catch (e: unknown) {
      context.changes.addPadError(this.pad, e);
      return;
    }

    if (constraints.value) {
      context.changes.addPadError(this.pad, constraints.value.reason);
      reject(constraints.value.reason);
    } else {
      await resolve(constraints);
    }
  }
}

export class UnlinkedPadSource extends PSource<DataValue> {
  constructor(
    id: PipeId,
    private readonly pad: Pad<'input'>,
  ) {
    super(id);
  }

  protected async collate(context: PipeContext): Promise<void> {
    const unlinked = this.pad.unlinked;

    if (
      unlinked?.value?.kind === 'array' ||
      unlinked?.value?.kind === 'plate'
    ) {
      // Find all plated materials inside arrays and reserve their well
      const check = (
        v: DataValue,
        c: DataConstraints,
        share: boolean,
      ): DataValue => {
        if (v.kind === 'array') {
          let identical = true;
          const copied = [];
          for (let i = 0; i < v.elements.length; ++i) {
            const element = v.elements[i];
            // Since we got an array of items, avoid sharing them with each other. In practice this
            // occurs with loading beads for a bead separation.
            const e = check(
              element,
              (c.array ?? [])[i] ?? {},
              /* share= */ false,
            );
            identical = identical && element === e;
            copied.push(e);
          }
          return identical ? v : {kind: 'array', elements: copied};
        } else if (v.kind === 'plate') {
          let identical = true;
          const copied = new Map<WellId, PlatedMaterialValue>();
          for (const [id, well] of v.wells) {
            // All material nodes populate the value on a well, so allow sharing
            const update = check(
              well,
              c.plate?.wells?.get(id) ?? {},
              share,
            ) as PlatedMaterialValue;
            identical = identical && well === update;
            copied.set(id, update);
          }
          return identical ? v : {kind: 'plate', id: v.id, wells: copied};
        } else if (v.kind === 'plated_material') {
          let well;
          if (share) {
            well = context.lab.plate(v.plate).getSharedWell(v.material.id);
          } else {
            well = context.lab.plate(v.plate).reserveWell();
          }
          return v.well === well.id ? v : {...v, well: well.id};
        } else {
          return v;
        }
      };
      unlinked.value = check(
        unlinked.value,
        unlinked.constraints,
        /* share= */ true,
      );
    } else if (unlinked?.value?.kind === 'plated_material') {
      // For any top level plated materials, use a shared well
      const v = unlinked.value;
      const well = context.lab.plate(v.plate).getSharedWell(v.material.id);
      if (v.well !== well.id) {
        unlinked.value = {
          ...v,
          well: well.id,
        };
      }
    }

    let constraints;
    try {
      constraints = await this.gatherConstraints(
        unlinked?.value,
        context,
        /* includeValue= */ true,
      );
    } catch (e: unknown) {
      context.changes.addPadError(this.pad, e);
      return;
    }

    const value = constraints.value?.expected || unlinked?.value;
    if (value) {
      const check = (v: DataValue, c: DataConstraints): void => {
        if (v.kind === 'array') {
          for (let i = 0; i < v.elements.length; ++i) {
            check(v.elements[i], (c.array ?? [])[i] ?? {});
          }
        } else if (v.kind === 'plate') {
          for (const [id, well] of v.wells) {
            check(well, c.plate?.wells?.get(id) ?? {});
          }
        } else if (v.kind === 'plated_material') {
          // Make a group per load to enable parallelization
          context.dispatcher
            .actor(this)
            .loadMaterial(
              v.material,
              context.lab.plate(v.plate).well(v.well),
              c.volume?.numberNl ?? 0,
            );
        }
      };
      check(value, constraints);
      context.changes.setValue(this.pad, value, constraints);
    }

    if (constraints.type) {
      // TODO(april): move into context.changes?
      this.pad.type = constraints.type;
    }
  }
}

export class SingleValueTransformer<T extends DataValue> extends PTransformer<
  DataValue,
  T
> {
  constructor(
    id: PipeId,
    private readonly kind: T['kind'],
    sources: Array<Source<DataValue>>,
  ) {
    super(id, sources);
  }

  async collate(context: PipeContext): Promise<void> {
    const {value, resolve, reject} = getSingleValue<T>(this.kind, this.values);
    return this.gatherConstraints(
      value,
      context,
      /* includeValue= */ true,
    ).then(resolve, reject);
  }
}

function getSingleValue<T extends DataValue>(
  kind: T['kind'],
  values: Map<
    Source<DataValue>,
    {
      value: Readonly<DataValue> | undefined;
      resolve: (value: DataConstraints) => Promise<void>;
      reject: (reason: unknown) => void;
    }
  >,
): {
  value: Readonly<T>;
  resolve: (value: DataConstraints) => Promise<void>;
  reject: (reason: unknown) => void;
} {
  if (values.size !== 1) {
    for (const {reject} of values.values()) {
      reject(new Error(`Expected 1 value, got ${values.size}`));
    }
  }

  const {value, resolve, reject} = getOnlyElement(values.values());
  if (value?.kind !== kind) {
    reject(new Error(`Expected a ${kind}, got ${value?.kind}`));
  }

  return {value: value as Readonly<T>, resolve, reject};
}

export function mergeConstraints(
  source: DataConstraints,
  destination: DataConstraints,
): void {
  if (source.array) {
    if (!destination.array) {
      destination.array = Array(source.array.length)
        .fill(undefined)
        .map(() => ({}));
    }
    checkArgument(source.array.length === destination.array.length);
    for (let i = 0; i < source.array.length; ++i) {
      mergeConstraints(source.array[i], destination.array[i]);
    }
  }

  if (source.plate) {
    if (!destination.plate) {
      destination.plate = {
        wells: new Map(),
      };
    }
    for (const [id, constraint] of source.plate.wells) {
      const existing = destination.plate.wells.get(id) ?? {};
      mergeConstraints(constraint, existing);
      destination.plate.wells.set(id, existing);
    }
  }

  if (source.type) {
    checkState(!destination.type, 'The type constraint has already been set');
    destination.type = source.type;
  }

  if (source.value) {
    checkState(!destination.value, 'The value constraint has already been set');
    destination.value = source.value;
  }

  if (source.volume) {
    if (!destination.volume) {
      destination.volume = {numberNl: 0};
    }
    destination.volume.numberNl += source.volume.numberNl;
  }
}

export function walkMaterials(
  value: DataValue | undefined,
  reject: (e: unknown) => void,
  fn: (v: PlatedMaterialValue) => DataConstraints | void,
): DataConstraints {
  if (!value) {
    return {};
  }

  const dive = (v: DataValue, inside: DataConstraints) => {
    if (v.kind === 'array') {
      inside.array = Array(v.elements.length);
      for (let i = 0; i < v.elements.length; ++i) {
        inside.array[i] = {};
        dive(v.elements[i], inside.array[i]);
      }
    } else if (v.kind === 'plate') {
      inside.plate = {wells: new Map()};
      for (const [id, material] of v.wells) {
        const constraint = {};
        inside.plate.wells.set(id, constraint);
        dive(material, constraint);
      }
    } else if (v.kind === 'plated_material') {
      Object.assign(inside, fn(v));
    } else {
      reject(new Error(`Expected plated_material data type, got ${v.kind}`));
    }
  };
  const root = {};
  dive(value, root);
  return root;
}
